import { type ComputedRef, computed } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { useDrainPages } from '@/composables/useDrainPages'
import { fetchBookingTypes, type BookingType } from '@/api/bookingTypes'
import { bookingTypesPollingInterval } from '@/config/api'

/**
 * Drains the full `['bookingTypes']` list for callers that need the whole
 * catalog at once — a lookup-by-id (`BookingTypeCard`, `BookingTypeMobileScreen`)
 * or every type enumerated as sub-tabs (`BookingScheduleView`'s room pills) —
 * rather than one incrementally-scrolled page at a time. `BookingTab`'s own
 * types table is the opposite case (a real paginated `DynamicTable` with
 * infinite scroll) and keeps its own separate `useInfiniteQuery` instead of
 * this composable.
 *
 * Shares the `['bookingTypes']` query key with that table, so switching
 * between the table and any of the three callers above doesn't refetch.
 *
 * @param options.enabled - Defaults to always-enabled (every existing
 * caller only ever mounts inside the Booking feature, itself gated on
 * `BookingTypesView`/`BookingsView`). Pass `false` when the caller isn't
 * guaranteed the viewer holds `BookingTypesView` — e.g. the Groups pane,
 * reached via `ClientsView`/`GroupsView` alone — so a 403 from `GET
 * /booking-types` doesn't trip the shared axios interceptor's app-wide
 * navigation.
 */
export function useAllBookingTypes(options?: { enabled?: ComputedRef<boolean> | boolean }) {
  const query = useInfiniteQuery({
    queryKey: ['bookingTypes'],
    queryFn: ({ pageParam }) => fetchBookingTypes(pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: options?.enabled ?? true,
    staleTime: bookingTypesPollingInterval,
    refetchInterval: bookingTypesPollingInterval,
    refetchIntervalInBackground: false,
  })

  useDrainPages(query)

  const bookingTypes = computed<BookingType[]>(() =>
    (query.data.value?.pages ?? []).flatMap((page) => page.items ?? []),
  )

  /**
   * True while there's still more to drain — a caller looking up one id by
   * value should keep showing a loading state (not "not found") until this
   * goes false, since the id it wants may be sitting on a page that hasn't
   * arrived yet.
   */
  const isDraining = computed(() => query.isPending.value || query.hasNextPage.value === true)

  return {
    bookingTypes,
    isPending: query.isPending,
    isError: query.isError,
    isDraining,
  }
}
